<template>
    <div>
        <b-form-row v-if="workItem != null">
            <b-form-group label="Type"
                          label-for="workItemTypeDisplayName"
                          class="col-md-4">
                <b-form-input name="workItemTypeDisplayName"
                              :value="workItem.workItemTypeDisplayName"
                              readonly>
                </b-form-input>
            </b-form-group>
            <slot name="required-date-time">
                <b-form-group label="Required Date/Time"
                              label-for="requiredDateTimeUTC"
                              class="col-md-4">
                    <b-form-input name="requiredDateTimeUTC"
                                  :value="requiredDateTimeDisplayString"
                                  readonly>
                    </b-form-input>
                </b-form-group>
            </slot>
            <b-form-group label="Status"
                          label-for="status"
                          class="col-md-4">
                <b-form-input name="status"
                              :value="statusDisplayString"
                              readonly>
                </b-form-input>
            </b-form-group>
        </b-form-row>
        <sticky-panel v-slot="{ collapsed }" v-if="relatedCourtMatterId || relatedCourtSittingId" cssClass="sticky-panel mb-3">
            <court-matter-details ref="courtMatterDetails"
                                  :api-url="apiBaseUrl"
                                  :court-matter-id="relatedCourtMatterId"
                                  details="all"
                                  v-if="relatedCourtMatterId">
            </court-matter-details>
            <court-sitting-details ref="courtSittingDetails"
                                   :api-controller-url="apiControllerUrl('courtsitting')"
                                   :court-sitting-id="relatedCourtSittingId"
                                   :all-audio-quality-options="allAudioQualityOptions"
                                   :can-modify-court-sitting-restricted-mode="permissions.canModifyRestrictedMode && !readonly"
                                   :can-modify-court-sitting-audio-quality="permissions.canModifyAudioQuality && !readonly"
                                   v-if="relatedCourtSittingId && !collapsed"
                                   @modalOpen="setModalStatus">
            </court-sitting-details>
        </sticky-panel>
        <court-appearances ref="courtAppearances"
                           :api-controller-url="apiControllerUrl('courtappearance')"
                           :court-matter-id="relatedCourtMatterId"
                           disabled
                           hide-when-empty
                           class="mb-2"
                           v-if="relatedCourtMatterId">
        </court-appearances>

        <sticky-panel v-slot="{ collapsed }" v-if="relatedInterviewId" cssClass="sticky-panel mb-3">
            <interview-details ref="interviewDetails"
                               :api-url="apiBaseUrl"
                               :interview-id="relatedInterviewId"
                               :details="collapsed ? 'med' : 'all'"
                               v-if="relatedInterviewId">
            </interview-details>
        </sticky-panel>

        <sticky-panel v-if="relatedListCourtId" cssClass="sticky-panel mb-3" not-collapsible>
            <list-court-details ref="listCourtDetails"
                                :api-url="apiBaseUrl"
                                :list-court-id="relatedListCourtId"
                                v-if="relatedListCourtId">
            </list-court-details>
        </sticky-panel>

        <slot name="additional-information"></slot>

        <workitem-orders ref="workItemOrders"
                         :api-base-url="apiBaseUrl"
                         :work-item-id="workItemId"
                         :work-item-completed="(workItem != null) && (workItem.completedDateTimeUTC != null)"
                         :hide-delivery-info="hideOrderDeliveryInfo"
                         :all-audio-sub-status-options="allAudioSubStatusOptions"
                         :all-log-notes-sub-status-options="allLogNotesSubStatusOptions"
                         :all-release-approval-sub-status-options="allReleaseApprovalSubStatusOptions"
                         :all-deposit-sub-status-options="allDepositSubStatusOptions"
                         :all-vetting-sub-status-options="allVettingSubStatusOptions"
                         :can-view-sub-status-details="permissions.canViewOrderSubStatusDetails"
                         :can-view-turnarounds="permissions.canViewTurnarounds"
                         :can-modify-audio-sub-status="permissions.canModifyAudioOrderSubStatus"
                         :can-modify-log-notes-sub-status="permissions.canModifyLogNotesOrderSubStatus"
                         :can-modify-release-approval-sub-status="permissions.canModifyReleaseApprovalOrderSubStatus"
                         :can-modify-deposit-sub-status="permissions.canModifyDepositOrderSubStatus"
                         :can-modify-vetting-sub-status="permissions.canModifyVettingOrderSubStatus"
                         :can-view-delivery-contact-details="permissions.canViewOrderDeliveryContactDetails"
                         :can-modify-delivery-contacts="permissions.canModifyOrderDeliveryContacts"
                         :can-reset-delivered-status="permissions.canResetOrderDeliveredStatus"
                         :readonly="readonly"
                         @modalOpen="setModalStatus">
        </workitem-orders>

        <comments ref="comments"
                  :api-controller-url="apiBaseUrl + '/comment'"
                  :current-user-account-id="currentUserAccountId"
                  :work-item-id="workItemId"
                  :can-create-comment="permissions.canCreateComment"
                  :can-create-billing-information-comment="permissions.canCreateBillingInformationComment"
                  :can-edit-and-retract-all-comments="permissions.canEditAndRetractAllComments"
                  :disabled="readonly"
                  :default-comment-type="$enum.CommentType.WorkItem"
                  class="my-2"
                  @modalOpen="setModalStatus">
        </comments>

        <staff-workitem-assignments ref="staffWorkItemAssignments"
                                    :current-user-account-id="currentUserAccountId"
                                    :api-url="apiBaseUrl"
                                    :images-url="imagesUrl"
                                    :work-item-id="workItemId"
                                    :relevant-roles="assignmentRelevantRoles"
                                    :roles-with-audio-minutes="assignmentRolesWithAudioMinutes"
                                    :roles-with-round-robin-priority="assignmentRolesWithRoundRobinPriority"
                                    :readonly="!permissions.canManageAssignments || readonly || disableAssignment"
                                    v-if="(permissions.canViewAllDetails || permissions.canManageAssignments)"
                                    :assignment-option="assignmentOption"
                                    :live-assignment-date="liveAssignmentDate"
                                    :live-assignment-start-time="liveAssignmentStartTime"
                                    :live-assignment-end-time="liveAssignmentEndTime"
                                    :delayed-assignment-start-date="delayedAssignmentStartDate"
                                    :delayed-assignment-due-date="delayedAssignmentDueDate"
                                    :court-registry-id="courtRegistryId"
                                    :interview-organisation-id="interviewOrganisationId"
                                    :time-zone-id="timeZoneId"
                                    :default-live-assignment-response-time="defaultLiveAssignmentResponseTime"
                                    :default-delayed-assignment-response-time="defaultDelayedAssignmentResponseTime"
                                    :live-round-robin-mode="liveAssignmentRoundRobinMode"
                                    :delayed-assigned-audio-length-mode="delayedAssignmentAssignedAudioLengthMode"
                                    :all-default-response-time-options="allDefaultResponseTimeOptions"
                                    v-model="assignedStaff"
                                    @modalOpen="setModalStatus"
                                    :turn-allocation-mode="turnAllocationMode">
        </staff-workitem-assignments>

        <slot></slot>

        <attachments ref="attachments"
                     :all-attachment-classifications="allAttachmentClassificationOptions"
                     attachment-classification-selection="required"
                     :disabled="readonly"
                     :related-to-id="workItemId"
                     related-to="WorkItem"
                     :current-user-account-id="currentUserAccountId"
                     :can-add-attachments="(allAttachmentClassificationOptions != null) && (allAttachmentClassificationOptions.length > 0)"
                     :can-modify-attachment-types="['WorkItemAttachment']"
                     :can-modify-all-attachments="permissions.canModifyAllAttachments"
                     v-if="permissions.canViewAttachments"
                     @modalOpen="setModalStatus">
        </attachments>

        <slot name="action-buttons"></slot>

        <template v-if="workItem != null">
            <b-input-group size="sm" prepend="Completed At" class="mt-2" v-if="workItem.completedDateTimeUTC != null">
                <b-form-input :value="formatDateTime(workItem.completedDateTimeUTC, 'DD/MM/YYYY h:mmA', true)" readonly></b-form-input>
                <b-input-group-prepend is-text class="input-group-append">By</b-input-group-prepend>
                <b-form-input :value="workItem.completedByUserAccountDisplayName" readonly></b-form-input>
                <b-input-group-prepend is-text class="input-group-append">Last Update At</b-input-group-prepend>
                <b-form-input :value="formatDateTime(workItem.lastUpdateDateTimeUTC, 'DD/MM/YYYY h:mmA', true)" readonly></b-form-input>
                <b-input-group-prepend is-text class="input-group-append">By</b-input-group-prepend>
                <b-form-input :value="workItem.lastUpdateByUserAccountDisplayName" readonly></b-form-input>
            </b-input-group>
            <b-input-group size="sm" prepend="In Progress Since" class="mt-2" v-else-if="workItem.lastUpdateDateTimeUTC != null">
                <b-form-input :value="formatDateTime(workItem.inProgressSinceDateTimeUTC, 'DD/MM/YYYY h:mmA', true)" readonly></b-form-input>
                <b-input-group-prepend is-text class="input-group-append">By</b-input-group-prepend>
                <b-form-input :value="workItem.inProgressByUserAccountDisplayName" readonly></b-form-input>
                <b-input-group-prepend is-text class="input-group-append">Last Update At</b-input-group-prepend>
                <b-form-input :value="formatDateTime(workItem.lastUpdateDateTimeUTC, 'DD/MM/YYYY h:mmA', true)" readonly></b-form-input>
                <b-input-group-prepend is-text class="input-group-append">By</b-input-group-prepend>
                <b-form-input :value="workItem.lastUpdateByUserAccountDisplayName" readonly></b-form-input>
            </b-input-group>
        </template>
    </div>
</template>

<script>
    import Vue from 'vue';
    import axios from 'axios';
    import BootstrapVue from 'bootstrap-vue';
    
    import { ValidationProvider, ValidationObserver } from 'vee-validate';

    import { extend, localize } from 'vee-validate';
    import { required } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    localize('en', en);

    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';

    import CourtMatterDetails from '../CourtMatterDetails.vue'
    import CourtSittingDetails from '../CourtSittingDetails.vue'
    import CourtAppearances from '../CourtAppearances.vue'
    import InterviewDetails from '../InterviewDetails.vue'
    import ListCourtDetails from '../ListCourtDetails.vue'
    import WorkitemOrders from '../WorkitemOrders.vue'
    import StaffWorkitemAssignments from '../StaffWorkitemAssignments.vue'
    import Attachments from '../Attachments.vue'

    import Comments from '../Comments.vue'

    import StickyPanel from '../StickyPanel';

    Vue.use(BootstrapVue);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('court-matter-details', CourtMatterDetails);
    Vue.component('court-sitting-details', CourtSittingDetails);
    Vue.component('court-appearances', CourtAppearances);
    Vue.component('interview-details', InterviewDetails);
    Vue.component('list-court-details', ListCourtDetails);
    Vue.component('workitem-orders', WorkitemOrders);
    Vue.component('staff-workitem-assignments', StaffWorkitemAssignments);
    Vue.component('attachments', Attachments);
    Vue.component('comments', Comments);
    Vue.component('sticky-panel', StickyPanel);
    export default {
        name: 'WorksheetGeneralInformation',
        mixins: [ErrorsMixin, DateTimeMixin],
        model: {
            event: 'change',
            prop: 'generalData'
        },
        props: {
            apiBaseUrl: {
                type: String,
                required: true
            },
            imagesUrl: {
                type: String,
                required: true
            },
            fileDownloadUrl: {
                type: String,
                required: true
            },
            currentUserAccountId: {
                type: String,
                required: true
            },
            workItemId: {
                type: String,
                required: true
            },
            workItem: {
                type: Object,
                default: null
            },
            relatedCourtSittingId: {
                type: String,
                default: null
            },
            relatedCourtMatterId: {
                type: String,
                default: null
            },
            relatedInterviewId: {
                type: String,
                default: null
            },
            relatedListCourtId: {
                type: String,
                default: null
            },
            assignmentRelevantRoles: {
                type: Array,
                required: true
            },
            assignmentRolesWithAudioMinutes: {
                type: Array,
                required: true
            },
            assignmentRolesWithRoundRobinPriority: {
                type: Array,
                required: true
            },
            allAudioSubStatusOptions: {
                type: Array,
                default: null
            },
            allLogNotesSubStatusOptions: {
                type: Array,
                default: null
            },
            allReleaseApprovalSubStatusOptions: {
                type: Array,
                default: null
            },
            allDepositSubStatusOptions: {
                type: Array,
                default: null
            },
            allVettingSubStatusOptions: {
                type: Array,
                default: null
            },
            portalUploadRequired: {
                type: Boolean,
                default: false,
            },
            hideOrderDeliveryInfo: {
                type: Boolean,
                default: false
            },
            readonly: {
                type: Boolean,
                default: false
            },
            disableAssignment: {
                type: Boolean,
                default: false
            },
            assignmentOption: {
                type: String,
                required: false
            },
            liveAssignmentDate: {
                type: Date,
                required: false,
            },
            liveAssignmentStartTime: {
                type: Date,
                required: false,
            },
            liveAssignmentEndTime: {
                type: Date,
                required: false,
            },
            delayedAssignmentStartDate: {
                type: Date,
                required: false,
            },
            delayedAssignmentDueDate: {
                type: Date,
                required: false,
            },
            courtRegistryId: {
                type: String,
                required: false,
                default: null
            },
            interviewOrganisationId: {
                type: String,
                required: false,
                default: null
            },
            timeZoneId: {
                type: String,
                required: false,
                default: null
            },
            defaultLiveAssignmentResponseTime: {
                type: Number
            },
            defaultDelayedAssignmentResponseTime: {
                type: Number
            },
            liveAssignmentRoundRobinMode: {
                type: Boolean,
                default: false
            },
            delayedAssignmentAssignedAudioLengthMode: {
                type: Boolean,
                default: false
            },
            allDefaultResponseTimeOptions: {
                type: Array,
                default: null
            },
            allAttachmentClassificationOptions: {
                type: Array,
                default: null
            },
            generalData: {
                type: Object,
                default: null
            },
            allAudioQualityOptions: {
                type: Array,
                default: null
            },
            turnAllocationMode: {
                type: Number,
                default: null
            }
        },
        computed: {
            requiredDateTimeDisplayString: function () {
                if (this.workItem == null) return null;
                if (this.workItem.requiredDateTimeUTC == null) return null;
                if (this.formatDateTime(this.workItem.requiredDateTimeUTC, 'YYYY-MM-DDTHH:mm:ss', true, true).indexOf('00:00:01') > 0) return this.formatDateTime(this.workItem.requiredDateTimeUTC, 'DD/MM/YYYY', true, true);
                return this.formatDateTime(this.workItem.requiredDateTimeUTC, 'DD/MM/YYYY h:mmA', true, false);
            },
            statusDisplayString: function () {
                if (this.workItem == null) return null;
                var statusDisplayString = this.workItem.statusDisplayName;
                if (this.portalUploadRequired && (this.workItem.portalUploadStatus != null)) {
                    statusDisplayString += " / ";
                    if (this.workItem.portalUploadStatus) statusDisplayString += this.$t("Entities.PortalUploadStatus.Uploaded");
                    else statusDisplayString += this.$t("Entities.PortalUploadStatus.NotUploaded");
                }
                return statusDisplayString;
            }
        },
        data: function () {
            return {
                permissions: this.generalData && this.generalData.permissions ? this.generalData.permissions : {},
                assignedStaff: this.generalData && this.generalData.assignedStaff ? this.generalData.assignedStaff : []
            };
        },
        watch: {
            permissions: function () {
                this.$emit('change', { assignedStaff: this.assignedStaff, permissions: this.permissions });
            },
            assignedStaff: function () {
                this.$emit('change', { assignedStaff: this.assignedStaff, permissions: this.permissions });
            }
        },
        methods: {

            setModalStatus: function (anyModalOpen) {
                //console.log('WGI: ' + anyModalOpen);
                this.$emit('modalOpen', anyModalOpen);
            },

            apiControllerUrl: function (apiRelativeUrl) {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += apiRelativeUrl;
                return url;
            },

            loadPermissions: function () {
                this.currentErrors = [];
                var url = this.$urlHelper.getApiUrl('workitem', 'permissions', [this.workItemId]);
                return axios
                    .get(url)
                    .then(response => {
                        if (response && response.data) {
                            this.permissions = response.data;
                        }
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length != 0) {
                            this.showErrorsToast(this.currentErrors);
                        }
                    });
            },

            reload: function () {
                var promises = [];
                promises.push(this.loadPermissions());
                if (this.relatedCourtMatterId) {
                    if (this.$refs.courtMatterDetails) promises.push(this.$refs.courtMatterDetails.reload());
                    if (this.$refs.courtAppearances) promises.push(this.$refs.courtAppearances.reload());
                }
                if (this.relatedCourtSittingId && this.$refs.courtSittingDetails) promises.push(this.$refs.courtSittingDetails.reload());
                if (this.relatedInterviewId && this.$refs.interviewDetails) promises.push(this.$refs.interviewDetails.reload());
                if (this.relatedListCourtId && this.$refs.listCourtDetails) promises.push(this.$refs.listCourtDetails.reload());
                if ((this.permissions.canViewAllDetails || this.permissions.canManageAssignments) && this.$refs.staffWorkItemAssignments) promises.push(this.$refs.staffWorkItemAssignments.reload());
                if (this.$refs.workItemOrders) promises.push(this.$refs.workItemOrders.reload());
                if (this.$refs.comments) promises.push(this.$refs.comments.reload());
                if (this.permissions.canViewAttachments && this.$refs.attachments) promises.push(this.$refs.attachments.reload());
                return Promise.all(promises);
            }
        },
        mounted: function () {
            if(this.permissions.canViewAttachments && this.$refs.attachments) this.$refs.attachments.reload();
        }
    }
</script>
